import {
  ARBITRUM_CHAIN_ID,
  BLESSNET_CHAIN_ID,
} from "@/features/bridge/constants/bridge.constant";
import { TOAST_VALIDATION_MSG } from "@/features/bridge/constants/toast-msgs.constants";

import { BridgeControlsType } from "@/features/bridge/providers/bridge.provider";
import { useMultiTokenManager } from "@/features/bridge/stores/multi-token-manager";
import { useNftTokenManager } from "@/features/bridge/stores/nft-token-manager";
import { useStandardTokenManager } from "@/features/bridge/stores/standard-token-manager.store";
import { useTokenManagerStore } from "@/features/bridge/stores/token-type.store";
import { TokenType } from "@/features/bridge/types/token-selector.types";
import { showToast } from "@components/layout/toast/show-toast";

import { ethers, parseEther } from "ethers";

const useValidation = (bridgeControls: BridgeControlsType) => {
  const {
    account: { isConnected, chainId: currentlySelectedChain },
    toChain: { destinationChain },
    fromChain: { originChain },
  } = bridgeControls;

  const { currentlySelectedTokenType } = useTokenManagerStore();
  const { amount, availableAmount } = useStandardTokenManager((state) => ({
    amount: state.tokenAmount,
    availableAmount: state.selectedToken?.balance,
  }));
  const nftCollections = useNftTokenManager((state) => state.collections);
  const { multiCollections } = useMultiTokenManager((state) => ({
    multiCollections: state.collections,
    isAnyTokensSelected: state.isAnyTokensSelected,
  }));

  const collections =
    currentlySelectedTokenType === TokenType.NFT
      ? nftCollections
      : multiCollections;

  /**
   * Checks if the wallet is connected and shows a toast notification if it is not.
   *
   * This function verifies whether the wallet connection status is active. If the wallet is not connected,
   * it triggers a toast notification to prompt the user to connect their wallet before proceeding.
   * The function returns a boolean value indicating the connection status:
   * - `true` if the wallet is connected
   * - `false` if the wallet is not connected
   *
   * @function isWalletConnected
   * @returns {boolean} `true` if the wallet is connected, otherwise `false`.
   */
  const isWalletConnected = (): boolean => {
    if (!isConnected) {
      showToast(TOAST_VALIDATION_MSG.WALLET_NOT_CONNECTED);
      return false;
    } else return true;
  };

  /**
   * Checks if both the origin and destination chains are selected.
   *
   * This function verifies whether both `originChain` and `destinationChain` are defined.
   * If either of them is not selected, it triggers a toast notification to prompt the user to select
   * both an origin and destination chain. The function returns a boolean value indicating whether both
   * chains are selected:
   * - `true` if both chains are selected
   * - `false` if either or both chains are not selected
   *
   * @function isBothChainsSelected
   * @returns {boolean} `true` if both the origin and destination chains are selected, otherwise `false`.
   */
  const isBothChainsSelected = (): boolean => {
    if (!destinationChain || !originChain) {
      showToast({
        toastContent: {
          message: "Please select both origin and destination chains",
          header: "Chains not selected",
          status: "warning",
        },
      });
      return false;
    } else return true;
  };

  const isOriginChainSelected = (): boolean => {
    if (!originChain) {
      showToast({
        toastContent: {
          message: "Please select an origin chain",
          header: "Origin chain not selected",
          status: "warning",
        },
      });
      return false;
    } else return true;
  };

  /**
   * Checks if the selected origin and destination chains are the same.
   *
   * This function compares the `chainId` of the `originChain` and `destinationChain`. If both chains
   * have the same `chainId`, it triggers a toast notification informing the user that they should select different chains.
   * The function returns a boolean value indicating whether the origin and destination chains are the same:
   * - `true` if the selected chains are the same
   * - `false` if the selected chains are different
   *
   * @function isSameChains
   * @returns {boolean} `true` if the selected chains are the same, otherwise `false`.
   */
  const isSameChains = (): boolean => {
    if (originChain?.chainId === destinationChain?.chainId) {
      showToast(TOAST_VALIDATION_MSG.SAME_CHAINS_WARNING);
      return true;
    } else return false;
  };

  /**
   * Checks if an amount has been entered for transfer.
   *
   * This function verifies whether the `amount` state variable has a value. If no amount is provided, it triggers
   * a toast notification informing the user to enter an amount. The function returns a boolean value indicating
   * whether an amount has been entered:
   * - `true` if an amount has been entered
   * - `false` if no amount has been entered
   *
   * @function isAmountInputted
   * @returns {boolean} `true` if an amount is provided, otherwise `false`.
   */
  const isAmountInputted = (): boolean => {
    if (!amount) {
      showToast(TOAST_VALIDATION_MSG.NO_AMOUNT_ENTERED_WARNING);
      return false;
    } else return true;
  };

  /**
   * Checks if the entered amount exceeds the available balance.
   *
   * This function compares the amount to bridge (in wei) with the available balance (in wei). If the amount exceeds
   * the balance, it shows a toast notification informing the user and returns `true`. Otherwise, it returns `false`.
   *
   * @function isAmountExceedingBalance
   * @returns {boolean} `true` if the amount exceeds the balance, otherwise `false`.
   */
  const isAmountExceedingBalance = (): boolean => {
    const tokenAmount = parseEther(amount.toString());
    const chainBalance = ethers.parseEther(availableAmount?.toString() || "0");
    if (tokenAmount > chainBalance) {
      showToast({
        toastContent: {
          message: (
            <>
              You don&apos;t have enough funds to bridge{" "}
              <span>{amount}BLESS</span>
            </>
          ),
          header: "Insufficient funds",
          status: "warning",
        },
      });
      return true;
    } else return false;
  };

  /**
   * Determines if bridging out to native is allowed based on the current chain configuration.
   *
   * This function checks if bridging out to the native chain is permissible. It verifies if the destination chain is Blessnet
   * and if the origin chain is not Arbitrum. If both conditions are met, it returns `true`, indicating that bridging out to
   * native is allowed. Otherwise, it shows a toast notification indicating that the operation is not permitted and returns `false`.
   *
   * @function canBridgeOutToNative
   * @returns {boolean} - `true` if bridging out to native is allowed, otherwise `false`.
   */
  const canBridgeOutToNative = (): boolean => {
    if (isDestinationChainBless() && !isOriginChainArbitrum()) return true;
    else {
      showToast(TOAST_VALIDATION_MSG.BRIDGE_OUT_NATIVE_ERROR);
      return false;
    }
  };

  /**
   * Checks if the origin chain is Arbitrum and the destination chain is Blessnet.
   *
   * This function verifies if the currently selected origin chain is Arbitrum and the destination chain is Blessnet.
   * It returns `true` if both conditions are met, otherwise it returns `false`.
   *
   * @function isArbToBless
   * @returns {boolean} - `true` if the origin chain is Arbitrum and the destination chain is Blessnet, otherwise `false`.
   */
  const isArbToBless = (): boolean => {
    return (
      originChain?.chainId === ARBITRUM_CHAIN_ID &&
      destinationChain?.chainId === BLESSNET_CHAIN_ID
    );
  };

  /**
   * Checks if the destination chain is Blessnet.
   *
   * This function verifies if the currently selected destination chain is Blessnet.
   * It returns `true` if the destination chain ID matches `BLESSNET_CHAIN_ID`, otherwise it returns `false`.
   *
   * @function isDestinationChainBless
   * @returns {boolean} - `true` if the destination chain is Blessnet, otherwise `false`.
   */
  const isDestinationChainBless = (): boolean => {
    return destinationChain?.chainId === BLESSNET_CHAIN_ID;
  };

  /**
   * Checks if the origin chain is Arbitrum.
   *
   * This function verifies if the currently selected origin chain is Arbitrum.
   * It returns `true` if the origin chain ID matches `ARBITRUM_CHAIN_ID`, otherwise it returns `false`.
   *
   * @function isOriginChainArbitrum
   * @returns {boolean} - `true` if the origin chain is Arbitrum, otherwise `false`.
   */
  const isOriginChainArbitrum = (): boolean => {
    return destinationChain?.chainId === ARBITRUM_CHAIN_ID;
  };

  /**
   * Checks if the origin chain is Blessnet.
   *
   * This function verifies if the currently selected origin chain is Blessnet.
   * It returns `true` if the origin chain ID matches `BLESSNET_CHAIN_ID`, otherwise it returns `false`.
   *
   * @function isOriginChainBless
   * @returns {boolean} - `true` if the origin chain is Blessnet, otherwise `false`.
   */
  const isOriginChainBless = (): boolean => {
    return originChain?.chainId === BLESSNET_CHAIN_ID;
  };

  /**
   * Determines if bridging out is possible given the selected chains.
   *
   * This function checks if either the active origin or destination chain is Blessnet.
   * If either chain is Blessnet, it shows a toast message indicating that bridging out is not possible
   * and suggests using `selfBridgeOutToNative`. It returns `false` in this case.
   * Otherwise, it returns `true`, indicating that bridging out is possible.
   *
   * @function canBridgeOut
   * @returns {boolean} - `true` if bridging out is possible, otherwise `false`.
   */
  const canBridgeOut = (): boolean => {
    if (
      (destinationChain?.chainId || originChain?.chainId) === BLESSNET_CHAIN_ID
    ) {
      showToast(TOAST_VALIDATION_MSG.BRIDGE_OUT_NATIVE_FROM_BLESSNET_ERROR);
      return false;
    } else return true;
  };

  /**
   * Checks if the currently selected network chain matches the specified origin chain.
   *
   * This function verifies whether the currently selected network chain (`currentlySelectedChain`) matches the
   * provided `chainId` or the chain ID of the currently active origin chain (`originChain`). If `chainId`
   * is not provided, it defaults to the chain ID of the currently active origin chain.
   *
   * @function isNetworkChainMatchSelected
   * @param {number} [chainId] - The chain ID to compare against the currently selected network chain.w
   *                              If not provided, the function will use the chain ID of the currently active origin chain.
   * @returns {boolean} - `true` if the currently selected network chain matches the specified or active origin chain,
   *                       otherwise `false`.
   */
  const isNetworkChainMatchSelected = (chainId?: number): boolean => {
    const chain = chainId ?? originChain?.chainId;
    return currentlySelectedChain === chain;
  };

  /**
   * Checks if a quote retrieval is valid based on the selected chains.
   *
   * This function determines if a quote can be retrieved by checking if the origin and destination chains
   * are Arbitrum and BlessNet, respectively. If this specific combination is detected, it shows a toast message
   * indicating that a quote cannot be retrieved between Arbitrum and BlessNet and returns `false`.
   * Otherwise, it returns `true`, indicating that the quote retrieval is valid.
   *
   * @function isValidQuote
   * @returns {boolean} - `true` if the quote retrieval is valid, otherwise `false`.
   */
  const isValidQuote = (): boolean => {
    if (isArbToBless()) {
      showToast(TOAST_VALIDATION_MSG.ARB_TO_BLESS_ERROR);
      return false;
    } else return true;
  };

  const isTokensSelected = (): boolean => {
    if (
      !Object.values(collections)
        .map((c) => c.tokenData)
        .flat()
        .some((token) => token.amountToBridge)
    ) {
      showToast(TOAST_VALIDATION_MSG.NO_TOKENS_SELECTED);
      return false;
    } else return true;
  };

  /**
   * Validates all required input parameters for the bridging process.
   *
   * This function performs a series of validation checks to ensure that all input parameters necessary for
   * the bridging process are valid. It checks if the wallet is connected, if both origin and destination chains
   * are selected, if the selected chains are different, if an amount is entered, if the entered amount does not
   * exceed the balance, and if sufficient funds are available.
   *
   * @function isInputParamsValid
   * @returns {boolean} - `true` if all validation checks pass and the input parameters are valid, otherwise `false`.
   */
  const isInputParamsValid = (tokenType: TokenType): boolean => {
    if (!isWalletConnected()) return false;
    if (!isBothChainsSelected()) return false;
    if (isSameChains()) return false;

    if (tokenType === TokenType.STANDARD) {
      if (!isAmountInputted()) return false; // Check if there is a non zero amount
      if (isAmountExceedingBalance()) return false; // Check if the amount is exceeding the balance
    } else if (tokenType === TokenType.MULTI) {
      if (!isTokensSelected()) return false;
    } else if (tokenType === TokenType.NFT) {
      if (!isTokensSelected()) return false;
    }
    return true;
  };

  return {
    isBothChainsSelected,
    isWalletConnected,
    isSameChains,
    isAmountInputted,
    isAmountExceedingBalance,
    isOriginChainSelected,
    isArbToBless,
    canBridgeOut,
    canBridgeOutToNative,
    isInputParamsValid,
    isNetworkChainMatchSelected,
    isValidQuote,
    isDestinationChainBless,
    isOriginChainBless,
  };
};

export default useValidation;
